import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { addFieldsInfo, deletePostedLedgerAction, Ledger, updateProcessing } from '@store/calculations/ledgerSlice';
import { toast } from 'react-toastify';
import { webJobIdAndFishingTripNumberDictionaryStorageName } from 'src/assets/config/dataConfig';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { EndOfTripClient, EndOfTripPostingWorkerClient, ReportsClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';

export interface State {
    isLoading: boolean;
    error: boolean;
    endOfTripList: Ledger[];
    webJobStatus: string;
}

const initialState: State = {
    isLoading: false,
    error: false,
    endOfTripList: [],
    webJobStatus: 'Unknown',
};

const endOfTripSlice = createSlice({
    name: 'endOfTrip',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getEndOfTripSuccess: (state, action: PayloadAction<Ledger[]>) => {
            state.endOfTripList = action.payload;
            state.isLoading = false;
        },
        postEndOfTripSuccess: (state, action: PayloadAction<string>) => {
            state.webJobStatus = action.payload;
            state.isLoading = false;
        },
        getWebJobStatusSuccess: (state, action: PayloadAction<string>) => {
            state.webJobStatus = action.payload;
            state.isLoading = false;
        },
    },
});

// Reducer
export default endOfTripSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
} = endOfTripSlice.actions;

// for useSelector
export const selectEndOfTrip = (state: RootState) => state.endOfTrip as State;

// ----------------------------------------------------------------------
export function getEndOfTrip(fishingTripNumber: string) {
    return async (dispatch, getState) => {
        dispatch(endOfTripSlice.actions.startLoading());
        try {
            const client = new EndOfTripClient(undefined, axios);
            const response = await client.estimateEndOfTrip(fishingTripNumber);
            const payload = await addFieldsInfo(dispatch, getState, response);

            dispatch(endOfTripSlice.actions.getEndOfTripSuccess(payload));
        } catch (error) {
            dispatch(endOfTripSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function postEndOfTrip(fishingTripNumber: string) {
    return async (dispatch, getState) => {
        dispatch(endOfTripSlice.actions.startLoading());
        try {
            const client = new EndOfTripPostingWorkerClient(undefined, axios);
            const webJobResponse = await client.startBackgroundTask(fishingTripNumber);
            dispatch(endOfTripSlice.actions.postEndOfTripSuccess(webJobResponse.statusName));

            // add web job id into browser storage
            const dictionary = localStorage.getItem(webJobIdAndFishingTripNumberDictionaryStorageName);
            const array = dictionary ? JSON.parse(dictionary) : {};
            array[fishingTripNumber] = webJobResponse.jobId;
            localStorage.setItem(webJobIdAndFishingTripNumberDictionaryStorageName, JSON.stringify(array, null, 2));
        } catch (error) {
            dispatch(endOfTripSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function deletePostedEndOfTripLedger(exportId: string, fishingTripNumber: string) {
    return async (dispatch, getState) => {
        dispatch(updateProcessing(true));
        const toastId = toast.loading(stringResources.REDUX_SLICE.DELETE_LOADING);
        try {
            await new EndOfTripClient(undefined, axios).deleteLedgerLines(exportId);
            await new ReportsClient(undefined, axios).delete(fishingTripNumber);
            dispatch(deletePostedLedgerAction(exportId));

            toast.update(toastId, {
                render: stringResources.REDUX_SLICE.DELETE_SUCCESS,
                type: 'success',
                isLoading: false,
                autoClose: 3000,
            });
        } catch (error) {
            dispatch(endOfTripSlice.actions.hasError(true));
            handleError(error, toastId);
        }
    };
}

export function postEndOfTripReportsCreate(fishingTripNumber: string, username : string) {
    return async (dispatch, getState) => {
        try {
        await new ReportsClient(undefined, axios).postEndOfTripReportsCreate(fishingTripNumber, username);
        } catch (error) {
            dispatch(endOfTripSlice.actions.hasError(true));
            handleError(error);
            return 'Error';
        }
    }
}

export function createOrUpdateCrewSettlementReport(fishingTripNumber: string, username : string) {
    return async (dispatch, getState) => {
        try {
        await new ReportsClient(undefined, axios).createOrUpdateCrewSettlementReport(fishingTripNumber, username);
        } catch (error) {
            dispatch(endOfTripSlice.actions.hasError(true));
            handleError(error);
            return 'Error';
        }
    }
}

export function createOrUpdateVesselManagerReport(fishingTripNumber: string, username : string) {
    return async (dispatch, getState) => {
        try {
        await new ReportsClient(undefined, axios).createOrUpdateVesselManagerReport(fishingTripNumber, username);
        } catch (error) {
            dispatch(endOfTripSlice.actions.hasError(true));
            handleError(error);
            return 'Error';
        }
    }
}

export function createOrUpdateWorkerSummaryReport(fishingTripNumber: string, username : string) {
    return async (dispatch, getState) => {
        try {
        await new ReportsClient(undefined, axios).createOrUpdateWorkerSummaryReport(fishingTripNumber, username);
        } catch (error) {
            dispatch(endOfTripSlice.actions.hasError(true));
            handleError(error);
            return 'Error';
        }
    }
}
export function getJobStatus(fishingTripNumber: string) {
    return async (dispatch, getState) => {
        try {
            const dictionary = localStorage.getItem(webJobIdAndFishingTripNumberDictionaryStorageName);
            const array = dictionary ? JSON.parse(dictionary) : {};
            const webJobId = array[fishingTripNumber];
            if (webJobId) {
                const client = new EndOfTripPostingWorkerClient(undefined, axios);
                const response = await client.getJobStatus(webJobId);
                dispatch(endOfTripSlice.actions.getWebJobStatusSuccess(response.statusName));
                return response.statusName;
            }
            return 'Unknown'; // no web job id found, initial case
        } catch (error) {
            dispatch(endOfTripSlice.actions.hasError(true));
            handleError(error);
            return 'Error';
        }
    };
}